import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { EmptyState } from "@dowty/ui";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import GRPC_BACKEND_URL, { SSO_ENABLED, SSO_ISSUER_ORIGIN } from "../grpcConfig";
import { readStorage, removeStorage, writeStorage } from "../utils/safeStorage";

export const SSO_SESSION_KEY = "saufdings-sso-session";
export const SILENT_SSO_KEY = "dowtex-sso-silent";
export const INTERACTIVE_SSO_KEY = "dowtex-sso-interactive";

const currentReturnPath = (): string => {
    const url = new URL(window.location.href);
    url.searchParams.delete("sso");
    url.searchParams.delete("sso_error");
    const path = `${url.pathname}${url.search}${url.hash}`;
    return path.startsWith("/") && !path.startsWith("//") && !path.includes("\\") && !Array.from(path).some((c) => c.codePointAt(0)! < 32 || c.codePointAt(0) === 127)
        ? path
        : "/";
};

export const dowtexHubUrl = (): string => window.location.hostname === "localhost" ? "http://localhost:3002" : "https://dowtex.com";

const buildDowtexLoginUrl = (): string => `${dowtexHubUrl()}/login?redirect=${encodeURIComponent(window.location.origin)}`;

/** Starts sign-in through the app API when SSO is on, otherwise the Dowtex login page. */
export const startSignIn = (silent = false) => {
    if (!silent) {
        removeStorage(SILENT_SSO_KEY, true);
    }
    if (SSO_ENABLED) {
        writeStorage(SSO_SESSION_KEY, "true", true);
        const form = document.createElement("form");
        form.method = "post";
        form.action = new URL("/sso/start", GRPC_BACKEND_URL).href;
        form.hidden = true;
        const returnTo = document.createElement("input");
        returnTo.type = "hidden";
        returnTo.name = "returnTo";
        returnTo.value = currentReturnPath();
        form.append(returnTo);
        if (silent) {
            const prompt = document.createElement("input");
            prompt.type = "hidden";
            prompt.name = "prompt";
            prompt.value = "none";
            form.append(prompt);
        }
        document.body.append(form);
        form.submit();
    } else {
        window.location.href = buildDowtexLoginUrl();
    }
};

export const submitGlobalSignOut = () => {
    const form = document.createElement("form");
    form.method = "post";
    form.action = new URL("/sso/signout", SSO_ISSUER_ORIGIN).href;
    form.hidden = true;
    const clientId = document.createElement("input");
    clientId.type = "hidden";
    clientId.name = "client_id";
    clientId.value = "saufdings";
    form.append(clientId);
    document.body.append(form);
    form.submit();
};

/** Empty state for account-only pages; start SSO once after a silent attempt. */
export function SignInRequired({ title, description }: Readonly<{ title: string; description: string }>) {
    const { t } = useTranslation();
    const { isAuthenticated, isLoading } = useAuth();
    const silentAttempt = typeof window === "undefined" ? null : readStorage(SILENT_SSO_KEY, true);
    const [interactiveTried, setInteractiveTried] = useState(() => typeof window !== "undefined"
        && readStorage(INTERACTIVE_SSO_KEY, true) === "1");
    useEffect(() => {
        if (SSO_ENABLED && (silentAttempt === "none" || silentAttempt === "tried")
            && !isAuthenticated && !isLoading && !interactiveTried) {
            writeStorage(INTERACTIVE_SSO_KEY, "1", true);
            setInteractiveTried(true);
            startSignIn();
        }
    }, [interactiveTried, isAuthenticated, isLoading, silentAttempt]);
    const message = interactiveTried ? t("auth.ssoInteractiveFailed") : description;
    return <EmptyState title={title} description={message} action={<Button type="primary" style={{ minHeight: 44 }} loading={isLoading} onClick={() => startSignIn()}>{t("auth.login")}</Button>} />;
}
